

/**
 * useAddNodeOnDrop - Hook to add a new node when an edge is dropped on empty space
 * Creates a new node at the drop position and connects it automatically.
 *
 * @module react-flow/hooks/useAddNodeOnDrop
 */

import { type Edge, type Node, useReactFlow } from '@xyflow/react';
import { useCallback, useRef } from 'react';

export interface UseAddNodeOnDropOptions {
  /** Callback to generate a new node ID */
  generateId?: () => string;
  /** Callback to create node data */
  createNodeData?: (sourceNodeId: string) => Record<string, unknown>;
  /** Default node type for new nodes */
  defaultNodeType?: string;
  /** Callback after node is created */
  onNodeCreated?: (node: Node) => void;
}

let nodeIdCounter = 0;
const defaultGenerateId = () => `node_${Date.now()}_${nodeIdCounter++}`;

/**
 * useAddNodeOnDrop Hook
 *
 * Usage:
 * ```tsx
 * function MyFlow() {
 *   const [nodes, setNodes] = useNodesState(initialNodes);
 *   const [edges, setEdges] = useEdgesState(initialEdges);
 *
 *   const {
 *     onConnectStart,
 *     onConnectEnd,
 *     connectingNodeId,
 *   } = useAddNodeOnDrop({
 *     defaultNodeType: 'default',
 *     onNodeCreated: (node) => console.warn('Created:', node),
 *   });
 *
 *   return (
 *     <div ref={wrapperRef}>
 *       <ReactFlow
 *         onConnectStart={onConnectStart}
 *         onConnectEnd={onConnectEnd}
 *         // ...other props
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
export function useAddNodeOnDrop(options: UseAddNodeOnDropOptions = {}) {
  const {
    generateId = defaultGenerateId,
    createNodeData = () => ({ label: 'New Node' }),
    defaultNodeType = 'default',
    onNodeCreated,
  } = options;

  const connectingNodeId = useRef<string | null>(null);
  const connectingHandleId = useRef<string | null>(null);
  const connectingHandleType = useRef<'source' | 'target' | null>(null);

  const { screenToFlowPosition, setNodes, setEdges } = useReactFlow();

  const onConnectStart = useCallback(
    (
      _: MouseEvent | TouchEvent,
      params: { nodeId: string | null; handleId: string | null; handleType?: 'source' | 'target' | null }
    ) => {
      connectingNodeId.current = params.nodeId;
      connectingHandleId.current = params.handleId;
      connectingHandleType.current = params.handleType || 'source';
    },
    []
  );

  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent) => {
      // Only process if we have a valid connecting node
      if (!connectingNodeId.current) return;

      // Get the target element
      const targetIsPane =
        (event.target as Element)?.classList?.contains('react-flow__pane');

      // Only create a new node if we dropped on the pane (not on another node/handle)
      if (targetIsPane) {
        // Get position from event
        const position = screenToFlowPosition({
          x: 'clientX' in event ? event.clientX : event.touches[0].clientX,
          y: 'clientY' in event ? event.clientY : event.touches[0].clientY,
        });


        const newNodeId = generateId();

        // Create the new node
        const newNode: Node = {
          id: newNodeId,
          type: defaultNodeType,
          position,
          data: createNodeData(connectingNodeId.current),
        };

        // Create the edge connecting source to new node
        const isSourceHandle = connectingHandleType.current === 'source';
        const newEdge: Edge = {
          id: `edge_${connectingNodeId.current}_${newNodeId}`,
          source: isSourceHandle ? connectingNodeId.current : newNodeId,
          target: isSourceHandle ? newNodeId : connectingNodeId.current,
          sourceHandle: isSourceHandle ? connectingHandleId.current : null,
          targetHandle: isSourceHandle ? null : connectingHandleId.current,
        };

        // Update nodes and edges
        setNodes((nodes) => [...nodes, newNode]);
        setEdges((edges) => [...edges, newEdge]);

        // Callback
        onNodeCreated?.(newNode);
      }

      // Reset
      connectingNodeId.current = null;
      connectingHandleId.current = null;
      connectingHandleType.current = null;
    },
    [screenToFlowPosition, generateId, createNodeData, defaultNodeType, setNodes, setEdges, onNodeCreated]
  );

  return {
    onConnectStart,
    onConnectEnd,
    connectingNodeId,
  };
}

export default useAddNodeOnDrop;
